import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft,
  Bell,
  MessageCircle,
  UserPlus,
  CheckCircle,
  XCircle,
  Loader2,
  Trash2,
  CheckCheck,
  Phone,
  MessageSquare,
  Heart
} from 'lucide-react';
import { notificationsAPI } from '../services/api';
import BottomNav from '../components/BottomNav';

const NotificationsPage = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [markingRead, setMarkingRead] = useState(false);

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    try {
      const data = await notificationsAPI.getAll();
      setNotifications(data);
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAllRead = async () => {
    setMarkingRead(true);
    try {
      await notificationsAPI.markAllRead();
      setNotifications(notifications.map(n => ({ ...n, read: true })));
    } catch (error) {
      console.error('Error marking all read:', error);
    } finally {
      setMarkingRead(false);
    }
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read
    if (!notification.read) {
      try {
        await notificationsAPI.markRead(notification.id);
        setNotifications(notifications.map(n => 
          n.id === notification.id ? { ...n, read: true } : n
        ));
      } catch (error) {
        console.error('Error marking read:', error);
      }
    }

    // Navigate based on type
    if (notification.type === 'new_message' || notification.type === 'join_approved') {
      navigate(`/group-chat/${notification.groupId}`);
    } else if (notification.type === 'join_request') {
      navigate(`/group-chat/${notification.groupId}`);
    } else if (notification.type === 'conversation_requested' && notification.relatedUserId) {
      // Navigate to direct message with the leader
      navigate(`/dm/${notification.relatedUserId}`);
    } else if (notification.type === 'direct_message' && notification.relatedUserId) {
      navigate(`/dm/${notification.relatedUserId}`);
    } else if (notification.type === 'reaction') {
      if (notification.groupId) {
        navigate(`/group-chat/${notification.groupId}`);
      } else if (notification.relatedUserId) {
        navigate(`/dm/${notification.relatedUserId}`);
      }
    }
  };

  const handleDelete = async (e, notificationId) => {
    e.stopPropagation();
    try {
      await notificationsAPI.delete(notificationId);
      setNotifications(notifications.filter(n => n.id !== notificationId));
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'new_message':
        return <MessageCircle className="w-5 h-5 text-blue-500" />;
      case 'join_request':
        return <UserPlus className="w-5 h-5 text-purple-500" />;
      case 'join_approved':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'join_denied':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'conversation_requested':
        return <Phone className="w-5 h-5 text-amber-500" />;
      case 'direct_message':
        return <MessageSquare className="w-5 h-5 text-navy-600" />;
      case 'reaction':
        return <Heart className="w-5 h-5 text-red-500" />;
      default:
        return <Bell className="w-5 h-5 text-gray-500" />;
    }
  };

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return date.toLocaleDateString([], { weekday: 'short' });
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-navy-900" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-br from-navy-900 to-navy-800 text-white px-4 py-4 sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-6 h-6" />
          </Button>
          <div className="flex-1">
            <h1 className="font-bold text-xl">Notifications</h1>
            {unreadCount > 0 && (
              <p className="text-sm text-tan-400">{unreadCount} unread</p>
            )}
          </div>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleMarkAllRead}
              className="text-tan-400 hover:bg-white/10"
              disabled={markingRead}
            >
              {markingRead ? (
                <Loader2 className="w-4 h-4 animate-spin mr-1" />
              ) : (
                <CheckCheck className="w-4 h-4 mr-1" />
              )}
              Mark all read
            </Button>
          )}
        </div>
      </div>

      <div className="p-4 space-y-3">
        {notifications.length === 0 ? (
          <Card className="border-0 shadow-lg rounded-2xl">
            <CardContent className="p-8 text-center">
              <Bell className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 text-lg font-medium">No notifications</p>
              <p className="text-gray-400 text-sm">You're all caught up!</p>
            </CardContent>
          </Card>
        ) : (
          notifications.map((notification) => (
            <Card
              key={notification.id}
              className={`border-0 shadow-md rounded-xl cursor-pointer transition-all hover:shadow-lg ${
                !notification.read ? 'bg-blue-50' : 'bg-white'
              }`}
              onClick={() => handleNotificationClick(notification)}
              data-testid={`notification-${notification.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                    {getNotificationIcon(notification.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${!notification.read ? 'font-semibold' : ''} text-navy-900`}>
                          {notification.message}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {notification.groupName} • {formatTime(notification.createdAt)}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {!notification.read && (
                          <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="w-8 h-8 text-gray-400 hover:text-red-500 hover:bg-red-50"
                          onClick={(e) => handleDelete(e, notification.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <BottomNav />
    </div>
  );
};

export default NotificationsPage;
